import React from "react";
import {css, Theme} from "@emotion/react";

import {Col, Row} from "@pg-design/grid-module";
import {calculateRemSize, flex, mh, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/e2e-test-utils/src/rp/common/test_id";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {ListingChangingButtons} from "../../../search/components/ListingChangingButtons";
import {useAppSelector} from "../../../store/hooks";
import {InvestmentOfferCategory, InvestmentOfferSubCategory, investmentOfferTypes, InvestmentOfferTypeSlug} from "../../constants/investment_offer_constants";
import {IInvestmentOfferListFiltersFields} from "../../hooks/use_investment_offer_filters";
import {getInvestmentOffersSubCategoryOptions} from "../utils/get_investment_offers_subcategory_options";
import {InvestmentOfferFiltersDrawer} from "./InvestmentOfferFiltersDrawer";

interface IProps extends IFormProps<IInvestmentOfferListFiltersFields> {
    setInvestmentOffersBlendActive: (state: boolean) => void;
    isMap?: boolean;
    mapUrl?: string;
    listUrl?: string;
    isSticky?: boolean;
}

const CATEGORY_FILTER_NAME = "category";
const SUB_CATEGORY_FILTER_NAME = "subcategory";

export const InvestmentOfferListDesktopFilters = form((props: IProps) => {
    const {isSticky} = props;
    const categorySelectProps = props.getFieldProps(CATEGORY_FILTER_NAME);
    const subCategorySelectProps = props.getFieldProps(SUB_CATEGORY_FILTER_NAME);
    const isAbroad = categorySelectProps.value === InvestmentOfferTypeSlug.ABROAD;
    const {isMobile} = useUserDevice();

    const categoryOptions = investmentOfferTypes.map((type) => ({value: type.slug, isActive: type.slug === categorySelectProps.value, label: type.namePlural}));
    const subCategoryOptions = getInvestmentOffersSubCategoryOptions(categorySelectProps.value as InvestmentOfferTypeSlug);
    const list = useAppSelector((state) => state.investmentOffer.list.offers);

    return (
        <Row css={searchSectionWrapper}>
            <Col lg={16} css={colWrapper}>
                <Col lg={4} md={4} sm={4} css={buttonsColumnWrapper}>
                    <ListingChangingButtons isMap={props.isMap} />
                </Col>
                {/* TODO: this whole component needs to be replaced with new searchDrawer, changed for Select due to hotfix */}
                <Col lg={8} md={5} sm={3} css={colPadding}>
                    <div css={selectWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INVESTMENT}>
                        <Select
                            {...subCategorySelectProps}
                            value={subCategorySelectProps.value ? subCategoryOptions.find(({value}) => value === subCategorySelectProps.value) : ""}
                            options={subCategoryOptions}
                            onChange={(selectedSubCategory) => {
                                let value = "" as InvestmentOfferSubCategory;

                                if (selectedSubCategory) {
                                    value = (selectedSubCategory as unknown as IOption).value as InvestmentOfferSubCategory;
                                }

                                subCategorySelectProps.onChange(SUB_CATEGORY_FILTER_NAME, value);
                                subCategorySelectProps.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, value);
                            }}
                            isClearable
                            isBorderless
                            placeholder={isMobile ? "Lokalizacja" : "Wybierz lokalizację"}
                            instanceId={SUB_CATEGORY_FILTER_NAME}
                            onMenuClose={() => props.setInvestmentOffersBlendActive(false)}
                            onMenuOpen={() => props.setInvestmentOffersBlendActive(true)}
                            css={locationSelectStyle}
                        />
                        <Select
                            {...categorySelectProps}
                            value={categorySelectProps.value ? categoryOptions.find(({value}) => value === categorySelectProps.value) : ""}
                            onChange={(selectedCategory) => {
                                let value = "" as InvestmentOfferCategory;

                                if (selectedCategory) {
                                    value = (selectedCategory as unknown as IOption).value as InvestmentOfferCategory;
                                }

                                categorySelectProps.onChange(CATEGORY_FILTER_NAME, value);
                                categorySelectProps.onAfterChange?.(CATEGORY_FILTER_NAME, value);
                            }}
                            options={categoryOptions}
                            instanceId={CATEGORY_FILTER_NAME}
                            isBorderless
                            css={categorySelectStyle}
                        />
                    </div>
                </Col>
                <Col css={isSticky ? stickySpacing : pv(0)} lg={2} sm={1} md={2}>
                    <div css={filtersAndButtonsStyle}>
                        <InvestmentOfferFiltersDrawer
                            latestQuery={list.latestQuery}
                            isAbroad={isAbroad}
                            mapUrl={props.mapUrl}
                            listUrl={props.listUrl}
                            isMap={props.isMap}
                        />
                    </div>
                </Col>
            </Col>
        </Row>
    );
});
const stickySpacing = (theme: Theme) => css`
    ${mt(0)};
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(0)}
    }
`;

const searchSectionWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${mh("auto")};
        position: relative;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colPadding = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(0)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
        ${ph(2)};
    }
`;

const filtersAndButtonsStyle = (theme: Theme) => css`
    ${flex()};
    gap: ${calculateRemSize(2)};
    justify-content: center;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${ph(0)};
        display: flex;
        justify-content: start;
        flex-wrap: wrap;
        row-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        column-gap: ${calculateRemSize(5)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: space-between;
        ${pv(0.5)};
    }
`;

const buttonsColumnWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(1)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const selectWrapper = () => css`
    display: flex;
    flex-direction: row;
    border-radius: 8px;
    background-color: white;
    border: solid var(--colors-gray-500) 1px;
`;

const locationSelectStyle = (theme: Theme) => css`
    border-right: var(--colors-gray-600) 1px solid;

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 70%;
    }
`;

const categorySelectStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        width: 30%;
    }
`;
import React from "react";
import {css, Theme} from "@emotion/react";

import {Col, Row} from "@pg-design/grid-module";
import {calculateRemSize, flex, mh, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/e2e-test-utils/src/rp/common/test_id";
import {form, IFormProps} from "@pg-mono/form";
import {useUserDevice} from "@pg-mono/user-device";

import {ListingChangingButtons} from "../../../search/components/ListingChangingButtons";
import {useAppSelector} from "../../../store/hooks";
import {InvestmentOfferCategory, InvestmentOfferSubCategory, investmentOfferTypes, InvestmentOfferTypeSlug} from "../../constants/investment_offer_constants";
import {IInvestmentOfferListFiltersFields} from "../../hooks/use_investment_offer_filters";
import {getInvestmentOffersSubCategoryOptions} from "../utils/get_investment_offers_subcategory_options";
import {InvestmentOfferFiltersDrawer} from "./InvestmentOfferFiltersDrawer";

interface IProps extends IFormProps<IInvestmentOfferListFiltersFields> {
    setInvestmentOffersBlendActive: (state: boolean) => void;
    isMap?: boolean;
    mapUrl?: string;
    listUrl?: string;
    isSticky?: boolean;
}

const CATEGORY_FILTER_NAME = "category";
const SUB_CATEGORY_FILTER_NAME = "subcategory";

export const InvestmentOfferListDesktopFilters = form((props: IProps) => {
    const {isSticky} = props;
    const categorySelectProps = props.getFieldProps(CATEGORY_FILTER_NAME);
    const subCategorySelectProps = props.getFieldProps(SUB_CATEGORY_FILTER_NAME);
    const isAbroad = categorySelectProps.value === InvestmentOfferTypeSlug.ABROAD;
    const {isMobile} = useUserDevice();

    const categoryOptions = investmentOfferTypes.map((type) => ({value: type.slug, isActive: type.slug === categorySelectProps.value, label: type.namePlural}));
    const subCategoryOptions = getInvestmentOffersSubCategoryOptions(categorySelectProps.value as InvestmentOfferTypeSlug);
    const list = useAppSelector((state) => state.investmentOffer.list.offers);

    return (
        <Row css={searchSectionWrapper}>
            <Col lg={16} css={colWrapper}>
                <Col lg={4} md={4} sm={4} css={buttonsColumnWrapper}>
                    <ListingChangingButtons isMap={props.isMap} />
                </Col>
                {/* TODO: this whole component needs to be replaced with new searchDrawer, changed for Select due to hotfix */}
                <Col lg={8} md={5} sm={3} css={colPadding}>
                    <div css={selectWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INVESTMENT}>
                        <Select
                            {...subCategorySelectProps}
                            value={subCategorySelectProps.value ? subCategoryOptions.find(({value}) => value === subCategorySelectProps.value) : ""}
                            options={subCategoryOptions}
                            onChange={(selectedSubCategory) => {
                                let value = "" as InvestmentOfferSubCategory;

                                if (selectedSubCategory) {
                                    value = (selectedSubCategory as unknown as IOption).value as InvestmentOfferSubCategory;
                                }

                                subCategorySelectProps.onChange(SUB_CATEGORY_FILTER_NAME, value);
                                subCategorySelectProps.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, value);
                            }}
                            isClearable
                            isBorderless
                            placeholder={isMobile ? "Lokalizacja" : "Wybierz lokalizację"}
                            instanceId={SUB_CATEGORY_FILTER_NAME}
                            onMenuClose={() => props.setInvestmentOffersBlendActive(false)}
                            onMenuOpen={() => props.setInvestmentOffersBlendActive(true)}
                            css={locationSelectStyle}
                        />
                        <Select
                            {...categorySelectProps}
                            value={categorySelectProps.value ? categoryOptions.find(({value}) => value === categorySelectProps.value) : ""}
                            onChange={(selectedCategory) => {
                                let value = "" as InvestmentOfferCategory;

                                if (selectedCategory) {
                                    value = (selectedCategory as unknown as IOption).value as InvestmentOfferCategory;
                                }

                                categorySelectProps.onChange(CATEGORY_FILTER_NAME, value);
                                categorySelectProps.onAfterChange?.(CATEGORY_FILTER_NAME, value);
                            }}
                            options={categoryOptions}
                            instanceId={CATEGORY_FILTER_NAME}
                            isBorderless
                            css={categorySelectStyle}
                        />
                    </div>
                </Col>
                <Col css={isSticky ? stickySpacing : pv(0)} lg={2} sm={1} md={2}>
                    <div css={filtersAndButtonsStyle}>
                        <InvestmentOfferFiltersDrawer
                            latestQuery={list.latestQuery}
                            isAbroad={isAbroad}
                            mapUrl={props.mapUrl}
                            listUrl={props.listUrl}
                            isMap={props.isMap}
                        />
                    </div>
                </Col>
            </Col>
        </Row>
    );
});
const stickySpacing = (theme: Theme) => css`
    ${mt(0)};
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(0)}
    }
`;

const searchSectionWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${mh("auto")};
        position: relative;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colPadding = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(0)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
        ${ph(2)};
    }
`;

const filtersAndButtonsStyle = (theme: Theme) => css`
    ${flex()};
    gap: ${calculateRemSize(2)};
    justify-content: center;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${ph(0)};
        display: flex;
        justify-content: start;
        flex-wrap: wrap;
        row-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        column-gap: ${calculateRemSize(5)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: space-between;
        ${pv(0.5)};
    }
`;

const buttonsColumnWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(1)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const selectWrapper = () => css`
    display: flex;
    flex-direction: row;
    border-radius: 8px;
    background-color: white;
    border: solid var(--colors-gray-500) 1px;
`;

const locationSelectStyle = (theme: Theme) => css`
    border-right: var(--colors-gray-600) 1px solid;

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 70%;
    }
`;

const categorySelectStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        width: 30%;
    }
`;
